/**
 * ═══════════════════════════════════════════════════════════════════════════════
 *  DEXEL WEB WORKER - BACKGROUND THREAD MATERIAL REMOVAL
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Bu worker, ağır dexel hesaplamalarını main thread'den ayırarak
 * 60fps render performansını korur.
 *
 * Features:
 * - Batch processing (50+ samples per message)
 * - Swept move support (linear interpolation)
 * - Ball / Bull / Flat tool geometry profiles
 * - Incremental height updates
 * - Dirty cell tracking
 */

/// <reference lib="webworker" />

// ─── Types ──────────────────────────────────────────────────────────────────
interface ToolSample {
  x: number;
  y: number;
  z: number;
  i: number;
  j: number;
  k: number;
}

interface DexelToolGeometry {
  type: 'flat' | 'ball' | 'bull';
  diameter: number;
  cornerRadius: number;
  length: number;
}

interface DexelBBox {
  minX: number; maxX: number;
  minY: number; maxY: number;
  minZ: number; maxZ: number;
}

interface DexelWorkerMsg {
  type: 'init' | 'process' | 'processSweptMove' | 'getHeightMap' | 'getDirty' | 'clearDirty' | 'dispose' | 'smoothAndAO';
  id: number;
  payload?: any;
}

interface DexelWorkerResp {
  type: 'result' | 'error' | 'heightUpdate';
  id: number;
  payload?: any;
}

// ─── Constants ──────────────────────────────────────────────────────────────
const MAX_SPANS_PER_COLUMN = 8;
const SPAN_SIZE = 2; // [spanMin, spanMax]

// ─── Worker State ───────────────────────────────────────────────────────────
let resX = 0;
let resY = 0;
let resZ = 0;
let cellSize = 0;
let bbox: DexelBBox = { minX: 0, maxX: 0, minY: 0, maxY: 0, minZ: 0, maxZ: 0 };
let silindirYonModu = 0; // 0: CNC Freze, 1: CNC Torna

// Data arrays - 3 dexel grid (XY, YZ, XZ)
let xyData: Float32Array | null = null;
let xySpanCounts: Uint8Array | null = null;
let yzData: Float32Array | null = null;
let yzSpanCounts: Uint8Array | null = null;
let xzData: Float32Array | null = null;
let xzSpanCounts: Uint8Array | null = null;
let dirtyMask: Uint8Array | null = null;
let heightMap: Float32Array | null = null;

// Dirty tracking
let dirtyCount = 0;
let dirtyBounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };

// Stats
let processedSamples = 0;
let spanModCount = 0;

// ─── Message Handler ────────────────────────────────────────────────────────
self.onmessage = (e: MessageEvent) => {
  const msg = e.data as DexelWorkerMsg;
  const type = msg.type;
  const id = msg.id as number;
  const payload = msg.payload;

  try {
    switch (type) {
      case 'init':
        handleInit(id, payload);
        break;
      case 'process':
        handleProcess(id, payload);
        break;
      case 'processSweptMove':
        handleProcessSweptMove(id, payload);
        break;
      case 'getHeightMap':
        handleGetHeightMap(id);
        break;
      case 'getDirty':
        handleGetDirty(id);
        break;
      case 'clearDirty':
        handleClearDirty(id);
        break;
      case 'dispose':
        handleDispose(id);
        break;
      case 'smoothAndAO':
        handleSmoothAndAO(id, payload);
        break;
      default:
        sendError(id, `Unknown message type: ${type}`);
    }
  } catch (err) {
    sendError(id, err instanceof Error ? err.message : String(err));
  }
};

// ─── Handlers ───────────────────────────────────────────────────────────────

function handleInit(id: number, payload: {
  resX: number;
  resY: number;
  resZ?: number;
  cellSize: number;
  bbox: DexelBBox;
  silindirYonModu: number;
}) {
  resX = payload.resX;
  resY = payload.resY;
  cellSize = payload.cellSize;
  bbox = payload.bbox;
  silindirYonModu = payload.silindirYonModu;

  // resZ hesapla (payload'da yoksa bbox'tan)
  resZ = payload.resZ || Math.ceil((bbox.maxZ - bbox.minZ) / cellSize);

  const xyCells = resX * resY;
  const yzCells = resY * resZ;
  const xzCells = resX * resZ;

  // Allocate XY grid
  xyData = new Float32Array(xyCells * MAX_SPANS_PER_COLUMN * SPAN_SIZE);
  xySpanCounts = new Uint8Array(xyCells);

  // Allocate YZ grid (X span'ları - takım X yönünde geldiğinde)
  yzData = new Float32Array(yzCells * MAX_SPANS_PER_COLUMN * SPAN_SIZE);
  yzSpanCounts = new Uint8Array(yzCells);

  // Allocate XZ grid (Y span'ları - takım Y yönünde geldiğinde)
  xzData = new Float32Array(xzCells * MAX_SPANS_PER_COLUMN * SPAN_SIZE);
  xzSpanCounts = new Uint8Array(xzCells);

  dirtyMask = new Uint8Array(xyCells);
  heightMap = new Float32Array(xyCells);

  // Initialize XY grid: Z span'ları [minZ, maxZ]
  for (let i = 0; i < xyCells; i++) {
    const offset = i * MAX_SPANS_PER_COLUMN * SPAN_SIZE;
    xyData[offset] = bbox.minZ;
    xyData[offset + 1] = bbox.maxZ;
    xySpanCounts[i] = 1;
    heightMap[i] = bbox.minZ;
  }

  // Initialize YZ grid: X span'ları [minX, maxX]
  for (let i = 0; i < yzCells; i++) {
    const offset = i * MAX_SPANS_PER_COLUMN * SPAN_SIZE;
    yzData[offset] = bbox.minX;
    yzData[offset + 1] = bbox.maxX;
    yzSpanCounts[i] = 1;
  }

  // Initialize XZ grid: Y span'ları [minY, maxY]
  for (let i = 0; i < xzCells; i++) {
    const offset = i * MAX_SPANS_PER_COLUMN * SPAN_SIZE;
    xzData[offset] = bbox.minY;
    xzData[offset + 1] = bbox.maxY;
    xzSpanCounts[i] = 1;
  }

  // Reset stats
  processedSamples = 0;
  spanModCount = 0;
  dirtyCount = 0;
  dirtyBounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };

  sendResult(id, { success: true, totalCells: xyCells });
}

function handleProcess(id: number, payload: {
  samples: ToolSample[];
  tool: DexelToolGeometry;
}) {
  if (!xyData || !xySpanCounts || !dirtyMask) {
    sendError(id, 'Worker not initialized');
    return;
  }

  const { samples, tool } = payload;
  let totalHits = 0;

  for (let si = 0; si < samples.length; si++) {
    totalHits += processToolPosition(samples[si], tool);
  }

  processedSamples += samples.length;

  const dirtyHeights = extractDirtyHeights();
  const transferables: Transferable[] = [];
  if (dirtyHeights.indices.buffer.byteLength > 0) {
    transferables.push(dirtyHeights.indices.buffer, dirtyHeights.heights.buffer, dirtyHeights.bottoms.buffer);
  }

  sendResult(id, {
    hits: totalHits,
    dirtyCount,
    dirtyHeights
  }, transferables);
}

/**
 * Swept move handler: prevPos → currPos arası interpolasyon yaparak işler
 * Main thread'deki processSweptMove'un worker karşılığı
 */
function handleProcessSweptMove(id: number, payload: {
  prevX: number; prevY: number; prevZ: number;
  currX: number; currY: number; currZ: number;
  axisX: number; axisY: number; axisZ: number;
  tool: DexelToolGeometry;
}) {
  if (!xyData || !xySpanCounts || !dirtyMask) {
    sendError(id, 'Worker not initialized');
    return;
  }

  const { prevX, prevY, prevZ, currX, currY, currZ, axisX, axisY, axisZ, tool } = payload;
  const R = tool.diameter / 2;

  const dx = currX - prevX;
  const dy = currY - prevY;
  const dz = currZ - prevZ;
  const length = Math.sqrt(dx * dx + dy * dy + dz * dz);

  const sample: ToolSample = { x: 0, y: 0, z: 0, i: axisX, j: axisY, k: axisZ };
  let totalHits = 0;

  if (length < 0.01) {
    // Same position
    sample.x = currX;
    sample.y = currY;
    sample.z = currZ;
    totalHits += processToolPosition(sample, tool);
  } else {
    // Adaptive step size (matches CPU engine)
    const stepSize = Math.min(cellSize * 0.5, R * 0.3);
    const numSteps = Math.min(Math.ceil(length / stepSize), 200);
    const invSteps = 1.0 / numSteps;

    for (let s = 0; s <= numSteps; s++) {
      const t = s * invSteps;
      sample.x = prevX + dx * t;
      sample.y = prevY + dy * t;
      sample.z = prevZ + dz * t;
      totalHits += processToolPosition(sample, tool);
    }
  }

  processedSamples++;

  const dirtyHeights = extractDirtyHeights();
  const transferables: Transferable[] = [];
  if (dirtyHeights.indices.buffer.byteLength > 0) {
    transferables.push(dirtyHeights.indices.buffer, dirtyHeights.heights.buffer, dirtyHeights.bottoms.buffer);
  }

  sendResult(id, {
    hits: totalHits,
    dirtyCount,
    dirtyHeights
  }, transferables);
}

/**
 * Tek tool pozisyonu işle - HER ZAMAN XY GRİD
 * Height map / mesh sadece XY grid okur, bu yüzden kesimler her zaman XY grid'e yazılır.
 * |k| >= 0.3 → üst/alt giriş, |k| < 0.3 → yan giriş (Z projeksiyon)
 */
function processToolPosition(sample: ToolSample, tool: DexelToolGeometry): number {
  if (!xyData || !xySpanCounts || !dirtyMask) return 0;

  let { x, y, z, i: axisI, j: axisJ, k: axisK } = sample;

  // NaN / zero-vector koruması
  if (isNaN(axisI) || isNaN(axisJ) || isNaN(axisK) ||
      (Math.abs(axisI) + Math.abs(axisJ) + Math.abs(axisK)) < 0.001) {
    axisI = 0; axisJ = 0; axisK = 1;
  }
  const R = tool.diameter / 2;
  const R2 = R * R;
  const isBallEnd = tool.type === 'ball';
  const isBullNose = tool.type === 'bull';
  const cornerR = tool.cornerRadius || 0;
  const flatRadius = R - cornerR;
  const flatR2 = flatRadius * flatRadius;
  const cornerR2 = cornerR * cornerR;
  const cellSizeInv = 1 / cellSize;

  // HER ZAMAN XY GRİD (Z derinlik) - mesh/heightmap uyumluluğu
  const depthMin = bbox.minZ;
  const depthMax = bbox.maxZ;
  const planeMinU = bbox.minX;
  const planeMinV = bbox.minY;
  const toolLen = tool.length || tool.diameter * 3;

  // Tool XY düzlem sınırları
  const iuMin = Math.max(0, Math.floor((x - R - planeMinU) * cellSizeInv));
  const iuMax = Math.min(resX - 1, Math.ceil((x + R - planeMinU) * cellSizeInv));
  const ivMin = Math.max(0, Math.floor((y - R - planeMinV) * cellSizeInv));
  const ivMax = Math.min(resY - 1, Math.ceil((y + R - planeMinV) * cellSizeInv));

  const absI = Math.abs(axisI);
  const absJ = Math.abs(axisJ);
  const absKW = Math.abs(axisK);
  let totalHits = 0;

  // ── HER ZAMAN XY grid'e yaz (mevcut mantık) ──
  for (let iv = ivMin; iv <= ivMax; iv++) {
    const rowOffset = iv * resX;
    const worldY = planeMinV + (iv + 0.5) * cellSize;
    const dv = worldY - y;
    const dvSquared = dv * dv;

    for (let iu = iuMin; iu <= iuMax; iu++) {
      const worldX = planeMinU + (iu + 0.5) * cellSize;
      const du = worldX - x;
      const dist2D_sq = du * du + dvSquared;

      if (dist2D_sq > R2) continue;

      let tipZ = z;

      if (isBallEnd) {
        tipZ = z - R + Math.sqrt(Math.max(0, R2 - dist2D_sq));
      } else if (isBullNose && cornerR > 0) {
        if (dist2D_sq > flatR2) {
          const dist2D = Math.sqrt(dist2D_sq);
          if (dist2D < R) {
            const distFromCornerCenter = dist2D - flatRadius;
            if (distFromCornerCenter < cornerR) {
              tipZ = z + Math.sqrt(cornerR2 - distFromCornerCenter * distFromCornerCenter) - cornerR;
            }
          }
        }
      }

      let cutMin: number, cutMax: number;
      if (absKW >= 0.3) {
        if (axisK >= 0) {
          if (tipZ >= depthMax) continue;
          cutMin = Math.max(tipZ, depthMin);
          cutMax = depthMax;
        } else {
          if (tipZ <= depthMin) continue;
          cutMin = depthMin;
          cutMax = Math.min(tipZ, depthMax);
        }
      } else {
        cutMin = Math.max(tipZ - toolLen * 0.5, depthMin);
        cutMax = Math.min(tipZ + toolLen * 0.5, depthMax);
      }

      if (cutMin >= cutMax) continue;

      totalHits++;
      const colIdx = rowOffset + iu;

      subtractSpanFromColumnGeneric(xyData, xySpanCounts, colIdx, cutMin, cutMax);

      markDirty(iu, iv);
    }
  }

  // ── X-dominant: YZ grid'e yaz (X span'ları) ──
  if (yzData && yzSpanCounts && absI >= 0.3 && absI >= absJ && absI >= absKW) {
    const yzCellInv = cellSizeInv;
    const iyMin = Math.max(0, Math.floor((y - R - bbox.minY) * yzCellInv));
    const iyMax = Math.min(resY - 1, Math.ceil((y + R - bbox.minY) * yzCellInv));
    const izMin = Math.max(0, Math.floor((z - R - bbox.minZ) * yzCellInv));
    const izMax = Math.min(resZ - 1, Math.ceil((z + R - bbox.minZ) * yzCellInv));

    for (let iz = izMin; iz <= izMax; iz++) {
      const worldZ = bbox.minZ + (iz + 0.5) * cellSize;
      const dw = worldZ - z;
      const dwSq = dw * dw;

      for (let iy = iyMin; iy <= iyMax; iy++) {
        const worldY = bbox.minY + (iy + 0.5) * cellSize;
        const dv = worldY - y;
        const dist2D_sq = dv * dv + dwSq;

        if (dist2D_sq > R2) continue;

        let tipX = x;
        if (isBallEnd) {
          tipX = x - R + Math.sqrt(Math.max(0, R2 - dist2D_sq));
        } else if (isBullNose && cornerR > 0) {
          if (dist2D_sq > flatR2) {
            const dist2D = Math.sqrt(dist2D_sq);
            if (dist2D < R) {
              const distFromCornerCenter = dist2D - flatRadius;
              if (distFromCornerCenter < cornerR) {
                tipX = x + Math.sqrt(cornerR2 - distFromCornerCenter * distFromCornerCenter) - cornerR;
              }
            }
          }
        }

        let cutMin: number, cutMax: number;
        if (axisI >= 0) {
          if (tipX >= bbox.maxX) continue;
          cutMin = Math.max(tipX, bbox.minX);
          cutMax = bbox.maxX;
        } else {
          if (tipX <= bbox.minX) continue;
          cutMin = bbox.minX;
          cutMax = Math.min(tipX, bbox.maxX);
        }

        if (cutMin >= cutMax) continue;

        const colIdx = iz * resY + iy;
        subtractSpanFromColumnGeneric(yzData, yzSpanCounts, colIdx, cutMin, cutMax);
      }
    }
  }

  // ── Y-dominant: XZ grid'e yaz (Y span'ları) ──
  if (xzData && xzSpanCounts && absJ >= 0.3 && absJ >= absI && absJ >= absKW) {
    const xzCellInv = cellSizeInv;
    const ixMin = Math.max(0, Math.floor((x - R - bbox.minX) * xzCellInv));
    const ixMax = Math.min(resX - 1, Math.ceil((x + R - bbox.minX) * xzCellInv));
    const izMin = Math.max(0, Math.floor((z - R - bbox.minZ) * xzCellInv));
    const izMax = Math.min(resZ - 1, Math.ceil((z + R - bbox.minZ) * xzCellInv));

    for (let iz = izMin; iz <= izMax; iz++) {
      const worldZ = bbox.minZ + (iz + 0.5) * cellSize;
      const dw = worldZ - z;
      const dwSq = dw * dw;

      for (let ix = ixMin; ix <= ixMax; ix++) {
        const worldX = bbox.minX + (ix + 0.5) * cellSize;
        const du = worldX - x;
        const dist2D_sq = du * du + dwSq;

        if (dist2D_sq > R2) continue;

        let tipY = y;
        if (isBallEnd) {
          tipY = y - R + Math.sqrt(Math.max(0, R2 - dist2D_sq));
        } else if (isBullNose && cornerR > 0) {
          if (dist2D_sq > flatR2) {
            const dist2D = Math.sqrt(dist2D_sq);
            if (dist2D < R) {
              const distFromCornerCenter = dist2D - flatRadius;
              if (distFromCornerCenter < cornerR) {
                tipY = y + Math.sqrt(cornerR2 - distFromCornerCenter * distFromCornerCenter) - cornerR;
              }
            }
          }
        }

        let cutMin: number, cutMax: number;
        if (axisJ >= 0) {
          if (tipY >= bbox.maxY) continue;
          cutMin = Math.max(tipY, bbox.minY);
          cutMax = bbox.maxY;
        } else {
          if (tipY <= bbox.minY) continue;
          cutMin = bbox.minY;
          cutMax = Math.min(tipY, bbox.maxY);
        }

        if (cutMin >= cutMax) continue;

        const colIdx = iz * resX + ix;
        subtractSpanFromColumnGeneric(xzData, xzSpanCounts, colIdx, cutMin, cutMax);
      }
    }
  }

  return totalHits;
}

function handleGetHeightMap(id: number) {
  if (!xyData || !xySpanCounts || !heightMap) {
    sendError(id, 'Worker not initialized');
    return;
  }

  const totalCells = resX * resY;

  for (let idx = 0; idx < totalCells; idx++) {
    const spanCount = xySpanCounts[idx];
    const offset = idx * MAX_SPANS_PER_COLUMN * SPAN_SIZE;

    if (spanCount === 0) {
      heightMap[idx] = bbox.minZ;
    } else if (spanCount === 1) {
      heightMap[idx] = xyData[offset + 1]; // spanMax
    } else {
      let maxOfAllSpans = -Infinity;
      for (let s = 0; s < spanCount; s++) {
        const spanMax = xyData[offset + s * SPAN_SIZE + 1];
        if (spanMax > maxOfAllSpans) maxOfAllSpans = spanMax;
      }
      heightMap[idx] = maxOfAllSpans;
    }
  }

  // Transfer the buffer (zero-copy)
  const buffer = heightMap.buffer.slice(0);
  sendResult(id, { heightMap: new Float32Array(buffer) }, [buffer]);
}

function handleGetDirty(id: number) {
  sendResult(id, {
    dirtyCount,
    dirtyBounds: { ...dirtyBounds }
  });
}

function handleClearDirty(id: number) {
  if (dirtyMask) {
    dirtyMask.fill(0);
  }
  dirtyCount = 0;
  dirtyBounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
  sendResult(id, { success: true });
}

function handleDispose(id: number) {
  xyData = null;
  xySpanCounts = null;
  yzData = null;
  yzSpanCounts = null;
  xzData = null;
  xzSpanCounts = null;
  dirtyMask = null;
  heightMap = null;
  resX = 0;
  resY = 0;
  resZ = 0;
  sendResult(id, { success: true });
}

// ─── Helper Functions ───────────────────────────────────────────────────────

/** Generic span subtraction - herhangi bir dexel grid üzerinde çalışır */
function subtractSpanFromColumnGeneric(
  data: Float32Array,
  spanCounts: Uint8Array,
  colIdx: number,
  cutMin: number,
  cutMax: number
): void {
  const spanCount = spanCounts[colIdx];
  if (spanCount === 0) return;

  const offset = colIdx * MAX_SPANS_PER_COLUMN * SPAN_SIZE;
  let modified = false;

  for (let s = spanCount - 1; s >= 0; s--) {
    const spanOffset = offset + s * SPAN_SIZE;
    const spanMin = data[spanOffset];
    const spanMax = data[spanOffset + 1];

    // No overlap
    if (cutMax <= spanMin || cutMin >= spanMax) continue;

    modified = true;

    // Complete removal
    if (cutMin <= spanMin && cutMax >= spanMax) {
      for (let j = s; j < spanCount - 1; j++) {
        const src = offset + (j + 1) * SPAN_SIZE;
        const dst = offset + j * SPAN_SIZE;
        data[dst] = data[src];
        data[dst + 1] = data[src + 1];
      }
      spanCounts[colIdx]--;
      continue;
    }

    // Cut from top
    if (cutMin <= spanMin && cutMax < spanMax) {
      data[spanOffset] = cutMax;
      continue;
    }

    // Cut from bottom
    if (cutMin > spanMin && cutMax >= spanMax) {
      data[spanOffset + 1] = cutMin;
      continue;
    }

    // Split (cut from middle)
    if (cutMin > spanMin && cutMax < spanMax) {
      if (spanCounts[colIdx] < MAX_SPANS_PER_COLUMN) {
        for (let j = spanCount - 1; j > s; j--) {
          const src = offset + j * SPAN_SIZE;
          const dst = offset + (j + 1) * SPAN_SIZE;
          data[dst] = data[src];
          data[dst + 1] = data[src + 1];
        }
        data[spanOffset + 1] = cutMin;
        const newSpanOffset = offset + (s + 1) * SPAN_SIZE;
        data[newSpanOffset] = cutMax;
        data[newSpanOffset + 1] = spanMax;
        spanCounts[colIdx]++;
      }
    }
  }

  if (modified) {
    spanModCount++;
  }
}

function markDirty(ix: number, iy: number): void {
  if (!dirtyMask) return;

  const idx = iy * resX + ix;
  if (dirtyMask[idx] === 0) {
    dirtyMask[idx] = 1;
    dirtyCount++;
    if (ix < dirtyBounds.minX) dirtyBounds.minX = ix;
    if (ix > dirtyBounds.maxX) dirtyBounds.maxX = ix;
    if (iy < dirtyBounds.minY) dirtyBounds.minY = iy;
    if (iy > dirtyBounds.maxY) dirtyBounds.maxY = iy;
  }
}

function extractDirtyHeights(): { indices: Int32Array; heights: Float32Array; bottoms: Float32Array } {
  if (!xyData || !xySpanCounts || !dirtyMask) {
    return { indices: new Int32Array(0), heights: new Float32Array(0), bottoms: new Float32Array(0) };
  }

  const capacity = Math.max(dirtyCount, 1);
  const indices = new Int32Array(capacity);
  const heights = new Float32Array(capacity);
  const bottoms = new Float32Array(capacity);
  let wi = 0;

  const totalCells = resX * resY;
  for (let idx = 0; idx < totalCells; idx++) {
    if (dirtyMask[idx] === 0) continue;
    indices[wi] = idx;

    const spanCount = xySpanCounts[idx];
    const offset = idx * MAX_SPANS_PER_COLUMN * SPAN_SIZE;

    if (spanCount === 0) {
      heights[wi] = bbox.minZ;
      bottoms[wi] = bbox.minZ;
    } else if (spanCount === 1) {
      heights[wi] = xyData[offset + 1];
      bottoms[wi] = xyData[offset];
    } else {
      let maxH = -Infinity, minH = Infinity;
      for (let s = 0; s < spanCount; s++) {
        const sMin = xyData[offset + s * SPAN_SIZE];
        const sMax = xyData[offset + s * SPAN_SIZE + 1];
        if (sMax > maxH) maxH = sMax;
        if (sMin < minH) minH = sMin;
      }
      heights[wi] = maxH;
      bottoms[wi] = minH;
    }
    wi++;
  }

  dirtyMask.fill(0);
  dirtyCount = 0;
  dirtyBounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };

  return {
    indices: indices.subarray(0, wi),
    heights: heights.subarray(0, wi),
    bottoms: bottoms.subarray(0, wi)
  };
}

function handleSmoothAndAO(id: number, payload: {
  smoothIterations: number;
  aoRadius: number;
  dirtyBounds?: { minX: number; maxX: number; minY: number; maxY: number };
}) {
  if (!xyData || !xySpanCounts || !heightMap) {
    sendError(id, 'Worker not initialized');
    return;
  }

  const totalCells = resX * resY;
  const { smoothIterations, aoRadius, dirtyBounds: db } = payload;

  for (let idx = 0; idx < totalCells; idx++) {
    const spanCount = xySpanCounts[idx];
    const offset = idx * MAX_SPANS_PER_COLUMN * SPAN_SIZE;
    if (spanCount === 0) {
      heightMap[idx] = bbox.minZ;
    } else if (spanCount === 1) {
      heightMap[idx] = xyData[offset + 1];
    } else {
      let maxH = -Infinity;
      for (let s = 0; s < spanCount; s++) {
        const v = xyData[offset + s * SPAN_SIZE + 1];
        if (v > maxH) maxH = v;
      }
      heightMap[idx] = maxH;
    }
  }

  if (smoothIterations > 0) {
    const sigma = (1.0 / (resX > 0 ? ((bbox.maxX - bbox.minX) / resX) : 1)) * 0.6;
    const invSigSq = 1.0 / (sigma * sigma);

    const minY = db ? Math.max(2, db.minY - 2) : 2;
    const maxY = db ? Math.min(resY - 3, db.maxY + 2) : resY - 3;
    const minX = db ? Math.max(2, db.minX - 2) : 2;
    const maxX = db ? Math.min(resX - 3, db.maxX + 2) : resX - 3;

    const tempBuf = new Float32Array(totalCells);
    for (let iter = 0; iter < smoothIterations; iter++) {
      const src = iter === 0 ? heightMap : tempBuf;
      const dst = iter === 0 ? tempBuf : heightMap;
      for (let iy = 0; iy < resY; iy++) {
        for (let ix = 0; ix < resX; ix++) {
          const idx2 = iy * resX + ix;
          if (iy < minY || iy > maxY || ix < minX || ix > maxX || xySpanCounts[idx2] === 0) {
            dst[idx2] = src[idx2];
            continue;
          }
          const cH = src[idx2];
          let sum = 0, weight = 0;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              const nIdx = (iy + dy) * resX + (ix + dx);
              if (xySpanCounts[nIdx] === 0) continue;
              const diff = src[nIdx] - cH;
              const w = 1.0 / (1.0 + diff * diff * invSigSq);
              sum += src[nIdx] * w;
              weight += w;
            }
          }
          dst[idx2] = weight > 0 ? sum / weight : cH;
        }
      }
      if (smoothIterations === 1) heightMap.set(tempBuf);
    }
  }

  const aoMap = new Float32Array(totalCells);
  const marchSteps = Math.max(3, aoRadius);
  const stockTopZ = bbox.maxZ;
  const uncutThresh = stockTopZ - 0.01;
  const cellSz = (bbox.maxX - bbox.minX) / resX;
  const dxArr = [1, 0, -1, 0];
  const dyArr = [0, 1, 0, -1];

  const aoMinY = db ? Math.max(0, db.minY - aoRadius) : 0;
  const aoMaxY = db ? Math.min(resY - 1, db.maxY + aoRadius) : resY - 1;
  const aoMinX = db ? Math.max(0, db.minX - aoRadius) : 0;
  const aoMaxX = db ? Math.min(resX - 1, db.maxX + aoRadius) : resX - 1;

  for (let iy = aoMinY; iy <= aoMaxY; iy++) {
    for (let ix = aoMinX; ix <= aoMaxX; ix++) {
      const idx2 = iy * resX + ix;
      if (xySpanCounts[idx2] === 0) continue;
      const cH = heightMap[idx2];
      if (cH >= uncutThresh) continue;

      let totalOcc = 0;
      for (let d = 0; d < 4; d++) {
        let maxTan = 0;
        for (let step = 1; step <= marchSteps; step++) {
          const sx = ix + dxArr[d] * step;
          const sy = iy + dyArr[d] * step;
          if (sx < 0 || sx >= resX || sy < 0 || sy >= resY) break;
          const nIdx = sy * resX + sx;
          if (xySpanCounts[nIdx] === 0) continue;
          const tanA = (heightMap[nIdx] - cH) / (step * cellSz);
          if (tanA > maxTan) maxTan = tanA;
        }
        if (maxTan > 0) totalOcc += maxTan / Math.sqrt(1 + maxTan * maxTan);
      }
      aoMap[idx2] = Math.min(1, totalOcc * 0.25);
    }
  }

  const hmBuf = heightMap.buffer.slice(0);
  const aoBuf = aoMap.buffer;
  sendResult(id, {
    heightMap: new Float32Array(hmBuf),
    aoMap: aoMap
  }, [hmBuf, aoBuf]);
}

function sendResult(id: number, payload: any, transfer?: Transferable[]): void {
  const response: DexelWorkerResp = { type: 'result', id, payload };
  if (transfer) {
    self.postMessage(response, transfer);
  } else {
    self.postMessage(response);
  }
}

function sendError(id: number, message: string): void {
  const response: DexelWorkerResp = { type: 'error', id, payload: { message } };
  self.postMessage(response);
}
